import { appConfig } from '../../helpers/app-config/app-config';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGlobalStore } from '../../store/global-store/global-store';
import { disconnectSlackUser, getSlackChannels, getSlackStatus, uninstallSlack } from './slack-integrations-client';
import type { UseSlackChannelsResult, UseSlackStatusResult } from './slack-integrations-client.type';

const SLACK_QUERY_KEY = 'slack-integration';
const IS_SLACK_INTEGRATIONS_ENABLED = appConfig.VITE_APP_ENV_NAME !== 'production';

/**
 * Hook to fetch Slack install + per-user-connect status for the current org.
 *
 * Overrides the app's global ``staleTime: 5min`` / ``refetchOnMount: false``
 * defaults — the post-OAuth redirect lands users back on the integrations
 * page expecting an immediate state change, so we always refetch on mount
 * and never serve stale cache.
 *
 * @param {boolean} enabled - When false, the query is paused.
 * @returns {UseSlackStatusResult} Status object plus the underlying React Query result.
 */
function useSlackStatus(enabled = IS_SLACK_INTEGRATIONS_ENABLED): UseSlackStatusResult {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const query = useQuery({
    queryKey: [SLACK_QUERY_KEY, 'status', organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return getSlackStatus(organizationId);
    },
    enabled: !!organizationId && enabled && IS_SLACK_INTEGRATIONS_ENABLED,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  return { data: query.data, query };
}

/**
 * Hook to fetch the channel list for the channel-picker UX.
 *
 * Returns 409 from the BE when the user has not completed OAuth — the consumer
 * should handle that as a "connect first" prompt rather than an error.
 *
 * @param {boolean} enabled - When false, the query is paused (e.g. user not connected yet).
 * @returns {UseSlackChannelsResult} Channels list plus the underlying React Query result.
 */
function useSlackChannels(enabled = true): UseSlackChannelsResult {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const query = useQuery({
    queryKey: [SLACK_QUERY_KEY, 'channels', organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return getSlackChannels(organizationId);
    },
    enabled: !!organizationId && enabled,
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  return { data: query.data?.channels, query };
}

/**
 * Hook to uninstall the org's Slack workspace (admin only).
 *
 * Invalidates the status query on success so the page reflects the new state.
 *
 * @returns {UseMutationResult<void, Error, void>} Mutation result.
 */
function useUninstallSlack(): UseMutationResult<void, Error, void> {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!organizationId) {
        throw new Error('Organization ID is required to uninstall Slack');
      }
      return uninstallSlack(organizationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SLACK_QUERY_KEY] });
    },
  });
}

/**
 * Hook to disconnect the current user's Slack account from the org.
 *
 * @returns {UseMutationResult<void, Error, void>} Mutation result.
 */
function useDisconnectSlackUser(): UseMutationResult<void, Error, void> {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!organizationId) {
        throw new Error('Organization ID is required to disconnect Slack account');
      }
      return disconnectSlackUser(organizationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SLACK_QUERY_KEY] });
    },
  });
}

export { useDisconnectSlackUser, useSlackChannels, useSlackStatus, useUninstallSlack };
